import { useMutation } from '@tanstack/react-query'
import { reactQueryRequest } from 'api/makeQuery'
import { addSeconds, isBefore } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import Pusher from 'singletons/Pusher'
import User from 'singletons/User'
import { downloadFileByUrl } from 'util/fileDownload'

export const usePqmReportApi = () => {
  const [waitingJobId, setWaitingJobId] = useState<string | null>(null)
  const [cachedFileUrl, setCachedFileUrl] = useState<{ url: string; expires: Date } | null>()
  const { providerId } = User

  const { mutate: getPqmReport, isPending } = useMutation({
    mutationFn: async () => {
      return await reactQueryRequest('getPqmReport', { providerId })
    },
    onSuccess: (data) => {
      setWaitingJobId(data?.jobId)
      toast.success('Download in progress. Stay on this page until the download is complete.', {
        position: 'top-center',
        toastId: `${data?.jobId}_toast`,
        autoClose: false,
      })
    },
    onError: () => {
      setWaitingJobId(null)
    },
  })

  const downloadPqmReport = useCallback(() => {
    const { url, expires } = cachedFileUrl || {}
    if (url && expires && isBefore(new Date(), new Date(expires))) {
      downloadFileByUrl(url)
      toast.success('Download is complete')
      return
    }

    setCachedFileUrl(null)
    getPqmReport()
  }, [cachedFileUrl, getPqmReport])

  useEffect(() => {
    const removeListener = Pusher.listen((e) => {
      switch (e?.event) {
        case 'provider-report-generated': {
          const { job_id, file_url } = e?.meta || {}

          if (job_id && waitingJobId && job_id === waitingJobId && file_url) {
            // real expiration time is 300 seconds, but it's better to expire it a bit earlier to avoid connection-related issues
            setCachedFileUrl({ url: file_url, expires: addSeconds(new Date(), 295) })
            toast.dismiss(`${job_id}_toast`)
            downloadFileByUrl(file_url)
            setWaitingJobId(null)
            toast.success('Download is complete')
          }
          break
        }
      }
    })

    return () => {
      removeListener()
    }
  }, [waitingJobId])

  return { downloadPqmReport, isLoadingPqmReport: !!isPending || !!waitingJobId }
}
